import { defaultFilterVert, Filter, type FilterSystem, GlProgram, type RenderSurface, Texture, TexturePool } from "pixi.js";
import { BLOOM, HEAT } from "../managers/lightingLogic";
import { GRADE_FRAGMENT, WAR_VERTEX } from "./warShaders";

/*
    The bloom and the heat haze (0.98.6, the lighting at High, with the shaders on: managers/lighting.ts): the war
    look's grade (the last pass over the whole world, war/warLook.ts) made here as a filter with two passes of its own
    before it.

     - the bloom: the frame's bright parts (a muzzle's flame, a tracer, a fire, a lamp, a blast, the lightning's
       flash, the sun's glow) picked out of the frame into a texture a quarter of the screen each way (the brightness
       over a threshold, with a soft knee under it so nothing pops), blurred wide in two passes there (cheap: a
       sixteenth of the pixels), and added back over the frame by the grade. Only what's really bright blooms: the
       mud, the uniforms and the rest are under the threshold
     - the heat haze: the grade reads a heat map (managers/lighting.ts draws it: white where a fire burns on screen)
       and pulls the picture a few pixels this way and that there, rising, so the air shimmers over a fire

    0.98.7 (the owner: "Lighting that shows each object's shape"): the grade also reads the normals' picture
    (managers/normalLook.ts) and the lights' relief (managers/lightFans.ts), and lights every slope by the sun
    (`sun`): the shapes' relief, set each frame by war/warLook.ts.

    Every pass is GLSL ES 1.0 as Pixi rewrites it (tests/src/update0986.test.ts): no loops, no version line.
*/

let flat: Texture | undefined;
/** a pixel of flat normal (straight up): what the grade reads where no picture of the shapes was drawn (0.98.7) */
export const flatNormalTexture = (): Texture => {
    if (flat && !flat.destroyed) return flat;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 2;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.fillStyle = "rgb(128,128,255)";
        ctx.fillRect(0, 0, 2, 2);
    }
    flat = Texture.from(canvas);
    flat.source.alphaMode = "no-premultiply-alpha";
    return flat;
};

/** the frame's bright parts: what's over the threshold (a soft knee under it), the rest black */
export const EXTRACT_FRAGMENT = /* glsl */`
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform float uThreshold;
uniform float uKnee;

void main(void) {
    vec4 s = texture(uTexture, vTextureCoord);
    vec3 c = s.a > 0.0 ? s.rgb / s.a : s.rgb;
    float l = max(c.r, max(c.g, c.b));
    float k = uThreshold * uKnee;
    float soft = clamp(l - uThreshold + k, 0.0, 2.0 * k);
    soft = soft * soft / (4.0 * k + 0.0001);
    float share = max(soft, l - uThreshold) / max(l, 0.0001);
    finalColor = vec4(c * share, 1.0);
}
`;

/** a blur along one way (`uStep`, a texel's measure times the spread): nine taps, a Gaussian's weights */
export const BLUR_FRAGMENT = /* glsl */`
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec2 uStep;

void main(void) {
    vec4 c = texture(uTexture, vTextureCoord) * 0.227;
    c += (texture(uTexture, vTextureCoord + uStep) + texture(uTexture, vTextureCoord - uStep)) * 0.1945;
    c += (texture(uTexture, vTextureCoord + uStep * 2.0) + texture(uTexture, vTextureCoord - uStep * 2.0)) * 0.1216;
    c += (texture(uTexture, vTextureCoord + uStep * 3.0) + texture(uTexture, vTextureCoord - uStep * 3.0)) * 0.054;
    c += (texture(uTexture, vTextureCoord + uStep * 4.0) + texture(uTexture, vTextureCoord - uStep * 4.0)) * 0.0162;
    finalColor = c;
}
`;

/** the blur's taps this many texels of the small texture apart (its texels are big: a wide, soft bloom) */
const SPREAD = 1.5;

/** the grade's uniforms, as war/warLook.ts fills them, with the bloom's and the heat's */
export type GradeUniforms = Record<string, { value: unknown, type: string }>;

/**
 * the war look's grade, with the bloom's passes before it and the heat map handed to it: `bloom` on, the frame's
 * bright parts are picked out and blurred first; `heat` set, the picture wavers where it says
 */
export class GradeFilter extends Filter {
    /** the bloom this frame (the lighting at High) */
    bloom = false;
    /** the heat map this frame (fires on screen, the lighting at High), or none */
    heat?: Texture;
    /** how bright a pixel is before it blooms (the sky's: `bloomThreshold`) */
    set threshold(value: number) {
        this._extract.resources.extractUniforms.uniforms.uThreshold = value;
    }

    /**
     * the shapes' relief this frame (0.98.7): the normals' picture and the lights' relief (none: lit flat), the sun's
     * slope (toward the light on screen, and its height: the stretch of a shadow per unit of height) and how boldly it
     * models the shapes
     */
    setRelief(normal: Texture | undefined, relief: Texture | undefined, sunX: number, sunY: number, stretch: number, strength: number, light: readonly [number, number, number]): void {
        const u = this.uniforms;
        this.resources.uNormal = (normal ?? flatNormalTexture()).source;
        this.resources.uRelief = (relief ?? Texture.EMPTY).source;
        const l = u.uSunL as Float32Array;
        const len = Math.hypot(sunX * stretch, sunY * stretch, 1);
        l[0] = (sunX * stretch) / len; l[1] = (sunY * stretch) / len; l[2] = 1 / len;
        u.uSunRelief = strength;
        u.uReliefOn = normal ? 1 : 0;
        const c = u.uLightColor as Float32Array;
        c[0] = light[0]; c[1] = light[1]; c[2] = light[2];
    }

    private readonly _extract: Filter;
    private readonly _blur: Filter;

    constructor(uniforms: GradeUniforms, resolution: number) {
        super({
            glProgram: GlProgram.from({ vertex: WAR_VERTEX, fragment: GRADE_FRAGMENT, name: "war-grade" }),
            resources: {
                uBloom: Texture.WHITE.source,
                uHeat: Texture.WHITE.source,
                uNormal: flatNormalTexture().source,
                uRelief: Texture.EMPTY.source,
                gradeUniforms: {
                    ...uniforms,
                    uBloomOn: { value: 0, type: "f32" },
                    uBloomStrength: { value: BLOOM.strength, type: "f32" },
                    uBloomScale: { value: new Float32Array([1, 1]), type: "vec2<f32>" },
                    uHeatOn: { value: 0, type: "f32" },
                    uHeatWobble: { value: HEAT.wobble, type: "f32" },
                    uSunL: { value: new Float32Array([0, 0, 1]), type: "vec3<f32>" },
                    uSunRelief: { value: 0, type: "f32" },
                    uReliefOn: { value: 0, type: "f32" },
                    uLightColor: { value: new Float32Array([1, 1, 1]), type: "vec3<f32>" }
                }
            },
            resolution
        });
        this._extract = new Filter({
            glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment: EXTRACT_FRAGMENT, name: "war-bloom-extract" }),
            resources: {
                extractUniforms: {
                    uThreshold: { value: BLOOM.threshold, type: "f32" },
                    uKnee: { value: BLOOM.knee, type: "f32" }
                }
            }
        });
        this._blur = new Filter({
            glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment: BLUR_FRAGMENT, name: "war-bloom-blur" }),
            resources: {
                blurUniforms: {
                    uStep: { value: new Float32Array([0, 0]), type: "vec2<f32>" }
                }
            }
        });
    }

    /** the grade's uniforms (war/warLook.ts sets the sky's, the mask's and the flashes' each frame) */
    get uniforms(): Record<string, unknown> {
        return this.resources.gradeUniforms.uniforms as Record<string, unknown>;
    }

    override apply(filterManager: FilterSystem, input: Texture, output: RenderSurface, clearMode: boolean): void {
        const u = this.uniforms;
        let bright: Texture | undefined;
        let blurred: Texture | undefined;
        if (this.bloom) {
            const width = input.frame.width, height = input.frame.height;
            bright = TexturePool.getOptimalTexture(width, height, BLOOM.scale, false);
            blurred = TexturePool.getOptimalTexture(width, height, BLOOM.scale, false);
            this._extract.apply(filterManager, input, bright, true);
            const step = this._blur.resources.blurUniforms.uniforms.uStep as Float32Array;
            step[0] = SPREAD / bright.source.pixelWidth;
            step[1] = 0;
            this._blur.apply(filterManager, bright, blurred, true);
            step[0] = 0;
            step[1] = SPREAD / bright.source.pixelHeight;
            this._blur.apply(filterManager, blurred, bright, true);
            this.resources.uBloom = bright.source;
            u.uBloomOn = 1;
            const scale = u.uBloomScale as Float32Array;
            scale[0] = input.source.width / bright.source.width;
            scale[1] = input.source.height / bright.source.height;
        } else u.uBloomOn = 0;
        if (this.heat && !this.heat.destroyed) {
            this.resources.uHeat = this.heat.source;
            u.uHeatOn = 1;
        } else u.uHeatOn = 0;
        filterManager.applyFilter(this, input, output, clearMode);
        if (bright) TexturePool.returnTexture(bright);
        if (blurred) TexturePool.returnTexture(blurred);
    }

    override destroy(): void {
        this._extract.destroy();
        this._blur.destroy();
        super.destroy();
    }
}
